import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Stack, Typography } from '@mui/material';
import { useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { forgotManagerPasswordDocument } from '@social-garden/api/documents/manager.ts';
import {
  ForgotPasswordFieldValues,
  ForgotPasswordSchema,
} from '@/constants/ValidationSchema.ts';
import EmailField from '@/components/EmailField.tsx';
import PageTitle from '@/components/PageTitle.tsx';

export const Route = createFileRoute('/_guest/forgot-password')({
  component: ForgotPassword,
});

function ForgotPassword() {
  const { t } = useTranslation(['manager']);
  const navigate = useNavigate();
  const [forgotManagerPassword, { loading }] = useMutation(
    forgotManagerPasswordDocument,
  );

  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<ForgotPasswordFieldValues>({
    resolver: zodResolver(ForgotPasswordSchema),
    mode: 'all',
  });

  const handleOnValid = useCallback(
    async ({ email }: ForgotPasswordFieldValues) => {
      const result = await forgotManagerPassword({
        variables: {
          input: {
            email,
          },
        },
      });

      if (result.data?.forgotManagerPassword) {
        await navigate({ to: '/recover-password', search: { email } });
      }
    },
    [forgotManagerPassword, navigate],
  );

  return (
    <Stack spacing={4}>
      <Stack spacing={1} alignItems="center">
        <PageTitle variant="h4" textAlign="center">
          {t('forgotPassword.header')}
        </PageTitle>
        <Typography variant="subtitle1" textAlign="center">
          {t('forgotPassword.description')}
        </Typography>
      </Stack>
      <Stack
        component="form"
        spacing={2}
        noValidate
        onSubmit={handleSubmit(handleOnValid)}>
        <Controller<ForgotPasswordFieldValues, 'email'>
          name="email"
          control={control}
          render={EmailField}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          loading={loading}
          disabled={!isValid}>
          {t('forgotPassword.submitButton')}
        </Button>
      </Stack>
    </Stack>
  );
}
